import urls from "../global/constants/UrlConstants";
import { getCallParams, getHeaderObject, makeCall } from "../utils/service";
import { store } from "../utils/store";

const base = urls.url_prod;

export async function downloadBulkSample(
  type: "leads" | "channel_partners" | "call_dataset",
  format: "xlsx" | "csv" = "xlsx"
) {
  const callParams = await getCallParams("GET");
  const path =
    format === "csv"
      ? `${base}/bulk-jobs/sample.csv?type=${type}&format=csv`
      : `${base}/bulk-jobs/sample.xlsx?type=${type}&format=xlsx`;
  const res = await fetch(path, callParams as any);
  if (!res.ok) throw new Error("Failed to download sample");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `sample-${type}.${format}`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function createBulkJob(formData: FormData) {
  const accessToken = store.getState().auth.accessToken;
  const headers: any = await getHeaderObject(accessToken, {});
  // Let browser set multipart boundary
  delete headers["Content-Type"];
  const res = await fetch(`${base}/bulk-jobs`, {
    method: "POST",
    headers,
    body: formData,
  });
  const json = await res.json();
  if (!res.ok || json.success === 0) {
    throw new Error(json.message || json.error_msg || "Upload failed");
  }
  return json;
}

export async function getBulkJobHeaders(id: string) {
  const callParams = await getCallParams("GET");
  return makeCall(`${base}/bulk-jobs/${id}/headers`, callParams);
}

export async function selectBulkJobSheets(id: string, sheetNames: string[]) {
  const callParams = await getCallParams("PUT", { sheetNames });
  return makeCall(`${base}/bulk-jobs/${id}/sheets`, callParams);
}

export async function saveBulkJobMapping(
  id: string,
  mapping: Record<string, string>
) {
  const callParams = await getCallParams("PUT", { mapping });
  return makeCall(`${base}/bulk-jobs/${id}/mapping`, callParams);
}

export async function saveBulkJobProjectOverrides(
  id: string,
  projectTokenOverrides: Record<string, string>
) {
  const callParams = await getCallParams("PUT", { projectTokenOverrides });
  return makeCall(`${base}/bulk-jobs/${id}/project-overrides`, callParams);
}

export async function previewBulkJob(id: string) {
  const callParams = await getCallParams("POST", {});
  return makeCall(`${base}/bulk-jobs/${id}/preview`, callParams);
}

export async function commitBulkJob(
  id: string,
  mode: "unique_only" | "all" | "discard" | "unique_and_membership_update" = "unique_only",
  options: {
    setName?: string;
    reuseExistingSet?: boolean;
    projectTokenOverrides?: Record<string, string>;
  } = {}
) {
  const callParams = await getCallParams("POST", {
    mode,
    ...(options.setName ? { setName: options.setName } : {}),
    ...(options.reuseExistingSet ? { reuseExistingSet: true } : {}),
    ...(options.projectTokenOverrides
      ? { projectTokenOverrides: options.projectTokenOverrides }
      : {}),
  });
  return makeCall(`${base}/bulk-jobs/${id}/commit`, callParams);
}

export async function getBulkJobProgress(id: string) {
  const callParams = await getCallParams("GET");
  return makeCall(`${base}/bulk-jobs/${id}/progress`, callParams);
}

export async function retryBulkJob(id: string) {
  const callParams = await getCallParams("POST", {});
  return makeCall(`${base}/bulk-jobs/${id}/retry`, callParams);
}

export async function downloadFailedReport(id: string) {
  const callParams = await getCallParams("GET");
  const res = await fetch(`${base}/bulk-jobs/${id}/failed.xlsx`, callParams as any);
  if (!res.ok) throw new Error("No failed report");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `failed-${id}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function downloadBulkJobFile(
  id: string,
  fileName?: string
) {
  const callParams = await getCallParams("GET");
  const res = await fetch(`${base}/bulk-jobs/${id}/file`, callParams as any);
  if (!res.ok) throw new Error("Original file not available");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName || `bulk-upload-${id}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function renameBulkJobSet(id: string, setName: string) {
  const callParams = await getCallParams("PUT", { setName });
  return makeCall(`${base}/bulk-jobs/${id}/set-name`, callParams);
}

export async function publishPartialBulkJob(id: string) {
  const callParams = await getCallParams("POST", {});
  return makeCall(`${base}/bulk-jobs/${id}/publish-partial`, callParams);
}

export async function listBulkJobRecords(
  id: string,
  params: {
    page?: number;
    pageSize?: number;
    search?: string;
    idsOnly?: boolean | number | string;
  } = {}
) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && String(v) !== "") {
      qs.set(k, String(v));
    }
  });
  const callParams = await getCallParams("GET");
  return makeCall(
    `${base}/bulk-jobs/${id}/records${qs.toString() ? `?${qs}` : ""}`,
    callParams
  );
}

export async function listBulkJobs(query: Record<string, any> = {}) {
  const qs = new URLSearchParams(
    Object.entries(query)
      .filter(([, v]) => v != null && v !== "")
      .map(([k, v]) => [k, String(v)])
  ).toString();
  const callParams = await getCallParams("GET");
  return makeCall(`${base}/bulk-jobs${qs ? `?${qs}` : ""}`, callParams);
}

export async function getBulkJob(id: string) {
  const callParams = await getCallParams("GET");
  return makeCall(`${base}/bulk-jobs/${id}`, callParams);
}
